import React, { useEffect, useState } from "react";
import {
  Container, Row, Col, Card, CardBody, CardTitle, Spinner, Input, Label,
  Button
} from "reactstrap";
import { Alert } from "@mui/material";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  Legend
} from "recharts";

import {
  getAllProjects,
  getAllUsers,
  ProjectDTO,
  UserProfileDTO,
  getAllTimeSheets,
  TimeSheetResponseDTO
} from "../../Api/ApiUrlConst";

import { useAuth } from "../../Context/AuthContext";
import Select from "react-select";


interface PieDataItem {
  name: string;
  value: number;
  [key: string]: string | number;
}

const COLORS = [
  "#0088FE", "#00C49F", "#FFBB28", "#FF8042",
  "#845EC2", "#D65DB1", "#FF6F91", "#4D96FF"
];

const AdminDashboardComponents: React.FC = () => {
  const { role } = useAuth();

  const [projects, setProjects] = useState<ProjectDTO[]>([]);
  const [users, setUsers] = useState<UserProfileDTO[]>([]);
  const [loading, setLoading] = useState(false);

  const [selectedUserId, setSelectedUserId] = useState("All");

  const [selectedProject, setSelectedProject] = useState<string | null>(null);

  const [timeSheets, setTimeSheets] = useState<TimeSheetResponseDTO[]>([]);

  const [clickedProject, setClickedProject] = useState<string | null>(null);
  const [selectedDraftingType, setSelectedDraftingType] = useState("");



  const getFirstDayOfMonth = () => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1)
      .toISOString()
      .split("T")[0];
  };

  const getToday = () => {
    return new Date().toISOString().split("T")[0];
  };


  const [startDate, setStartDate] = useState(getFirstDayOfMonth());
  const [endDate, setEndDate] = useState(getToday());


  const projectOptions = projects.map(p => ({
    value: p.projectID,
    label: p.projectName
  }));

  const [selectedProjects, setSelectedProjects] = useState<any[]>([]);
  const selectedProjectNames = selectedProjects.map(p => p.label);





  // ---------------- Load Projects & Users ----------------
  useEffect(() => {
    if (role !== "Admin") return;

    const fetchBaseData = async () => {
      setLoading(true);
      try {
        const [pRes, uRes] = await Promise.all([
          getAllProjects(),
          getAllUsers()
        ]);
        setProjects(pRes.data || []);
        setUsers(uRes.data || []);
      } finally {
        setLoading(false);
      }
    };

    fetchBaseData();
  }, [role]);



  // ---------------- Fetch Timesheets ----------------
  const fetchTimeSheets = async () => {
    const user = selectedUserId !== "All" ? selectedUserId : undefined;

    const data = await getAllTimeSheets(startDate, endDate, undefined, user);
    setTimeSheets(data || []);
  };



  useEffect(() => {
    fetchTimeSheets();
  }, [startDate, endDate, selectedProjects, selectedUserId]);


  const filteredTimeSheets = timeSheets.filter(t =>
    selectedProjectNames.length === 0 ||
    selectedProjectNames.includes(t.projectName)
  );

  const modellingTypes = [
    "modelling",
    "modelling update",
    "modelling review",
  ];

  const draftingTypes = [
    "drafting",
    "drafting update",
    "drafting review",
  ];

  const otherTypes = [
    "others",
    "cp request",
    "release",
    "training",
    "teamcentre",
    "client meeting",
    "management",
    "iso",
  ];




  // ---------------- PIE DATA ----------------

  // Project wise
  const projectPieData = Object.values(
    filteredTimeSheets.reduce<Record<string, PieDataItem>>((acc, t) => {
      const key = t.projectName;
      if (!acc[key]) acc[key] = { name: key, value: 0 };
      acc[key].value += Number(t.spentHours || t.hours || 0);
      return acc;
    }, {})
  );


  // Task wise (Drafting / Modeling etc)
  const taskPieData = Object.values(
    timeSheets
      .filter(t =>
        selectedProjectNames.length === 0 ||
        selectedProjectNames.includes(t.projectName)
      )
      .reduce<Record<string, PieDataItem>>((acc, t) => {
        const task = t.module || "Others";
        if (!acc[task]) acc[task] = { name: task, value: 0 };
        acc[task].value += Number(t.spentHours || t.hours || 0);
        return acc;
      }, {})
  );
  const subtaskPieData = Object.values(
    timeSheets
      .filter(t =>
        selectedProjectNames.length === 0 ||
        selectedProjectNames.includes(t.projectName)
      )
      .reduce<Record<string, PieDataItem>>((acc, t) => {
        const task = t.subTask || "Others";
        if (!acc[task]) acc[task] = { name: task, value: 0 };
        acc[task].value += Number(t.spentHours || t.hours || 0);
        return acc;
      }, {})
  );



const childsubtaskPieData = Object.values(
  filteredTimeSheets
    .filter(t => t.childSubTask) // only require child task
    .reduce<Record<string, PieDataItem>>((acc, t) => {
      const task = t.childSubTask || "Others";

      if (!acc[task]) acc[task] = { name: task, value: 0 };

      acc[task].value += Number(t.spentHours || t.hours || 0);

      return acc;
    }, {})
);


  const Modellinghours = Object.values(
    timeSheets
      .filter(t =>
        (selectedProjectNames.length === 0 ||
          selectedProjectNames.includes(t.projectName)) &&
        modellingTypes.includes(t.product?.toLowerCase() || "")
      )
      .reduce<Record<string, PieDataItem>>((acc, t) => {
        const task = t.product || "Modelling";
        if (!acc[task]) acc[task] = { name: task, value: 0 };
        acc[task].value += Number(t.spentHours || t.hours || 0);
        return acc;
      }, {})
  );

  const Draftinghours = Object.values(
    timeSheets
      .filter(t =>
        (selectedProjectNames.length === 0 ||
          selectedProjectNames.includes(t.projectName)) &&

        draftingTypes.includes(t.product?.toLowerCase() || "") &&

        (!selectedDraftingType ||
          t.draftingType === selectedDraftingType)
      )
      .reduce<Record<string, PieDataItem>>((acc, t) => {
        const task = t.product || "Drafting";

        if (!acc[task]) acc[task] = { name: task, value: 0 };

        acc[task].value += Number(t.spentHours || t.hours || 0);

        return acc;
      }, {})
  );


  const othershours = Object.values(
    timeSheets
      .filter(t =>
        (selectedProjectNames.length === 0 ||
          selectedProjectNames.includes(t.projectName)) &&
        otherTypes.includes(t.product?.toLowerCase() || "")
      )
      .reduce<Record<string, PieDataItem>>((acc, t) => {
        const task = t.product || "Others";
        if (!acc[task]) acc[task] = { name: task, value: 0 };
        acc[task].value += Number(t.spentHours || t.hours || 0);
        return acc;
      }, {})
  );




  // User wise (for selected project)
  const userPieData = Object.values(
    timeSheets
      .filter(t => !selectedProject || t.projectName === selectedProject)
      .reduce<Record<string, PieDataItem>>((acc, t) => {
        const key = t.userName;
        if (!acc[key]) acc[key] = { name: key, value: 0 };
        acc[key].value += Number(t.spentHours || t.hours || 0);
        return acc;
      }, {})
  );
  const mechuserPieData = Object.values(
    timeSheets
      .filter(
        t =>
          (
            selectedProjectNames.length === 0 ||
            selectedProjectNames.includes(t.projectName)
          ) &&
          (t.userDepartment === "Mechanical" ||
            t.userDepartment === "MECH")
      )
      .reduce<Record<string, PieDataItem>>((acc, t) => {
        const key = t.userName;
        if (!acc[key]) acc[key] = { name: key, value: 0 };
        acc[key].value += Number(t.spentHours || t.hours || 0);
        return acc;
      }, {})
  );


  const elecuserPieData = Object.values(
    timeSheets
      .filter(
        t =>
          (
            selectedProjectNames.length === 0 ||
            selectedProjectNames.includes(t.projectName)
          ) &&
          (t.userDepartment === "Electrical" ||
            t.userDepartment === "ELEC")
      )
      .reduce<Record<string, PieDataItem>>((acc, t) => {
        const key = t.userName;
        if (!acc[key]) acc[key] = { name: key, value: 0 };
        acc[key].value += Number(t.spentHours || t.hours || 0);
        return acc;
      }, {})
  );



  const projectTotals = Object.values(
    timeSheets.reduce<Record<string, { project: string; hours: number }>>(
      (acc, t) => {
        const key = t.projectName;

        if (!acc[key]) {
          acc[key] = { project: key, hours: 0 };
        }

        acc[key].hours += Number(t.spentHours || t.hours || 0);

        return acc;
      },
      {}
    )
  );


  // ---------------- Click handlers ----------------

  const onProjectClick = (data: any) => {
    setSelectedProject(data.name);     // existing logic
    setClickedProject(data.name);      // 👈 NEW (for showing name)
  };

  const excludedEmails = [
    "client@wocons.com",
    "itmanager@gmail.com",
    "krish@gmail.com",
    "renganathan@wocons.com",
    "gokul@feesworth.com",
    "muthupandu400@gmail.com",
    "ravindrababu@wocons.com",
    "santhoshs@wocons.com",
    "santhoshr@wocons.com",
    "itmanager@gmail.com",
    "client@wocons.com",
    "hr@wocons.com",
    "vishnu@wocons.com",
    "limya@wocons.com",
    "Perumal@wocons.com",
    "krishsengamuthu@gmail.com",
    "gokulkannan199805@gmail.com",
    "projectmanager@wocons.com",
    "Rizalde.Barrato@Donaldson.com",
    "Vishnu.Prasath@Donaldson.com",
    "limya.shilin1@donaldson.com",
    "abc@xy.com",
    "aishwarya.jahagirdar@wocons.com",
    "priyadharshini.k@wocons.com",
    "blessi.sam@wocons.com"
  ];

  const drawingTypeOptions = [
    { code: "6MM", label: "6MM", full: "Standard 6 mm thickness part drawing" },

    { code: "2SG", label: "2SG", full: "2 Subgroup assembly drawing " },

    { code: "3EA", label: "3EA", full: "3 Element assembly (Extended) " },

    { code: "4MA", label: "4MA", full: "4 Member assembly drawing " },

    { code: "8PP", label: "8PP", full: "8 Part package drawing " },

    { code: "COMP-WELD", label: "COMP-WELD", full: "Complicated weldments drawing " },

    { code: "1FA", label: "1FA"},

    { code: "Others", label: "Other" },
  ];

  const totalProjectHours = projectTotals.reduce(
    (sum, p) => sum + p.hours,
    0
  );

  const clearAll = () => {
    // chart selection reset
    setClickedProject(null);

    // filter reset
    setSelectedProjects([]);
    setSelectedUserId("All");
    setStartDate("");
    setEndDate("");

  };












  // ---------------- UI ----------------

  if (role !== "Admin") {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh" }}>
        <Alert severity="error">Only Admins can access this page</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner color="primary" />
      </Container>
    );
  }

  return (
    <Container fluid style={{ padding: "30px 20px", background: "#f8f9fb" }}>

      {/* Filters */}
      <Row className="mb-3">
        <Col md={3}>
          <Label>Project Name</Label>

          <Select
            isMulti
            options={projectOptions.filter(
    (project) =>
      !["Priceworth", "TaskManager","TaskManager Deploy"].includes(
        project.label
      )
  )}
            value={selectedProjects}
            onChange={(selected) => setSelectedProjects(selected as any[])}
            placeholder="Select projects..."
            getOptionLabel={(e) => e.label}
            getOptionValue={(e) => String(e.value)}
            styles={{
              control: (base) => ({
                ...base,
                maxHeight: "18px"
              })
            }}
          />

        </Col>


        <Col md={2}>
          <Label>User</Label>
          <Input
            type="select"
            value={selectedUserId}
            onChange={e => setSelectedUserId(e.target.value)}
          >
            <option value="All">All Users</option>
            {users
             .filter(u => !excludedEmails.includes(u.email))

              .sort((a, b) => a.name.localeCompare(b.name))
              .map(u => (
                <option key={u.userID} value={u.userID}>
                  {u.name}
                </option>
              ))}
          </Input>
        </Col>

        <Col md={2}>
          <Label>Start Date</Label>
          <Input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
          />
        </Col>

        <Col md={2}>
          <Label>End Date</Label>
          <Input
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
          />
        </Col>
        <Col md={1}>
          <Button
            color="secondary"
            outline
            onClick={clearAll}
            style={{ marginTop: "30px" }}
          >
            Clear
          </Button>

        </Col>

      </Row>


      {/* Charts */}
      <Row className="g-4">

        {/* Project Pie */}
        <Col xl="4">
          <Card className="shadow-sm rounded-4">
            <CardBody>
              <CardTitle className="d-flex justify-content-between align-items-center">
                <span><b>📁 Project Hours</b></span>

                {/* {clickedProject && (
                  <span
                    style={{
                      fontSize: "18px",
                      background: "#e7f1ff",
                      color: "#0d6efd",
                      padding: "3px 8px",
                      borderRadius: "12px",
                      fontWeight: 600
                    }}
                  >
                    {clickedProject}
                  </span>
                )} */}
              </CardTitle>

              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={projectPieData} dataKey="value" nameKey="name" outerRadius={100} onClick={onProjectClick}>
                    {projectPieData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: any) => `${v} hrs`} />
                </PieChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>
        </Col>

        {/* Task Pie */}
        <Col xl="4">
          <Card className="shadow-sm rounded-4">
            <CardBody>
              <CardTitle><b>🧩 Task  Hours</b></CardTitle>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={taskPieData} dataKey="value" nameKey="name" outerRadius={100} label>
                    {taskPieData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: any) => `${v} hrs`} />
                </PieChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>
        </Col>
        <Col xl="4">
          <Card className="shadow-sm rounded-4">
            <CardBody>
              <CardTitle><b>🧩 Subtask Hours</b></CardTitle>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={subtaskPieData} dataKey="value" nameKey="name" outerRadius={100} label>
                    {subtaskPieData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: any) => `${v} hrs`} />
                </PieChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>
        </Col>
        <Col xl="4">
          <Card className="shadow-sm rounded-4">
            <CardBody>
              <CardTitle><b>🧩 Child Task Hours</b></CardTitle>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={childsubtaskPieData} dataKey="value" nameKey="name" outerRadius={100} label>
                    {childsubtaskPieData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: any) => `${v} hrs`} />
                </PieChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>
        </Col>
        <Col xl="4">
          <Card className="shadow-sm rounded-4">
            <CardBody>
              <CardTitle><b> 🧩 Modelling Hours</b></CardTitle>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={Modellinghours} dataKey="value" nameKey="name" outerRadius={100} label>
                    {Modellinghours.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: any) => `${v} hrs`} />
                </PieChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>
        </Col>
        <Col xl="4">
          <Card className="shadow-sm rounded-4">
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <CardTitle className="mb-0">
                  <b> 🧩 Drafting Hours</b>
                </CardTitle>

                <Input
                  type="select"
                  value={selectedDraftingType}
                  onChange={(e) => setSelectedDraftingType(e.target.value)}
                  style={{ width: "200px" }}
                >
                  <option value="">All Drafting Types</option>
                  {drawingTypeOptions.map(opt => (
                    <option key={opt.code} value={opt.code}>
                      {opt.label}
                    </option>
                  ))}
                </Input>
              </div>

              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={Draftinghours} dataKey="value" nameKey="name" outerRadius={100} label>
                    {Draftinghours.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: any) => `${v} hrs`} />
                </PieChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>
        </Col>
        <Col xl="4">
          <Card className="shadow-sm rounded-4">
            <CardBody>
              <CardTitle><b>🧩 Others  Hours</b></CardTitle>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={othershours} dataKey="value" nameKey="name" outerRadius={100} label>
                    {othershours.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: any) => `${v} hrs`} />
                </PieChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>
        </Col>

        {/* User Pie */}
        <Col xl="4">
          <Card className="shadow-sm rounded-4">
            <CardBody>
              <CardTitle><b>👤 Mechanical Hours</b></CardTitle>

              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={mechuserPieData} dataKey="value" nameKey="name" outerRadius={100}>
                    {mechuserPieData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: any) => `${v} hrs`} />
                </PieChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>
        </Col>
        <Col xl="4">
          <Card className="shadow-sm rounded-4">
            <CardBody>
              <CardTitle><b>👤 Electrical Hours</b></CardTitle>

              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={elecuserPieData} dataKey="value" nameKey="name" outerRadius={100}>
                    {elecuserPieData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: any) => `${v} hrs`} />
                </PieChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>
        </Col>

        <Col xl="12">
          <Card className="shadow-sm rounded-4">
            <CardBody>
              <CardTitle className="fw-bold mb-3">
                📊 Project – Total Working Hours
              </CardTitle>

              {projectTotals.length === 0 ? (
                <p className="text-muted">No data available</p>
              ) : (
                <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                  {projectTotals.map(p => (
                    <div
                      key={p.project}
                      className="d-flex justify-content-between align-items-center border-bottom py-2"
                    >
                      <span className="fw-semibold">{p.project}</span>
                      <span className="badge bg-primary">
                        {p.hours} H
                      </span>
                    </div>
                  ))}

                  {/* TOTAL */}
                  <div className="d-flex justify-content-between align-items-center pt-3 mt-2 border-top fw-bold">
                    <span>Total Hours</span>
                    <span className="badge bg-success">
                      {totalProjectHours} H
                    </span>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>



      </Row>

    </Container>
  );
};

export default AdminDashboardComponents;
